import { cn, cva, NoddiIcon, NoddiIconName, type VariantProps } from 'noddi-ui-common';
import { Slot as SlotPrimitive } from 'radix-ui';
import { ButtonHTMLAttributes, forwardRef } from 'react';
import { webButtonStyling } from '../../helpers/webButtonStyling';
import { NoddiSpinner } from '../Feedback/NoddiSpinner';

// Don't add hover as effect persists on mobile after pressed, which makes the button look disabled.
const iconButtonVariants = cva(
  cn('flex shrink-0 items-center justify-center text-sm font-medium disabled:opacity-50', webButtonStyling),
  {
    variants: {
      variant: {
        primary: 'border border-button-primary bg-button-primary text-button-primary-foreground',
        outline: 'border border-input bg-background text-foreground shadow-xs',
        subtle: 'bg-gray-100 text-gray-600 hover:bg-gray-200',
        destructive: 'border border-destructive bg-destructive text-destructive-foreground',
        // Low-emphasis destructive: reads as an outline button but marks the action as dangerous.
        destructiveOutline: 'border border-destructive bg-background text-destructive shadow-xs',
        ghost: ''
      },
      size: {
        default: 'size-9',
        small: 'size-6'
      }
    },
    defaultVariants: {
      variant: 'outline',
      size: 'default'
    }
  }
);

interface Props extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof iconButtonVariants> {
  iconName: NoddiIconName;
  loading?: boolean;
  asChild?: boolean;
}

export const NoddiIconButton = forwardRef<HTMLButtonElement, Props>(
  (
    {
      iconName,
      size = 'default',
      variant = 'outline',
      loading,
      disabled,
      className,
      asChild = false,
      children,
      ...props
    },
    ref
  ) => {
    const resolvedVariant = variant ?? 'outline';
    const buttonClassName = cn(iconButtonVariants({ variant: resolvedVariant, size }), className);
    const Comp = asChild ? SlotPrimitive.Slot : 'button';
    const isDefaultSize = size === 'default';
    // Icon and spinner inherit the button's own text color, so a `className` that recolors
    // the button can never leave them behind — see the note in NoddiButton.
    const icon = loading ? (
      <NoddiSpinner className='text-current' size={isDefaultSize ? 'small' : 'xSmall'} />
    ) : (
      <NoddiIcon name={iconName} size={isDefaultSize ? 'small' : 'xSmall'} />
    );

    return (
      <Comp
        type={!asChild ? 'button' : undefined}
        className={buttonClassName}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {icon}
        {asChild ? <SlotPrimitive.Slottable>{children}</SlotPrimitive.Slottable> : null}
      </Comp>
    );
  }
);
NoddiIconButton.displayName = 'NoddiIconButton';
